import { useMemo } from 'react';
import { CreateActivityFormData } from '../../../../../../types/activity.types';
import { ACTIVITY_INITIAL_VALUES } from '../../../../../../types/activity.initialValues';

const parseDateTime = (isoString: string) => {
  const date = new Date(isoString);

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const dateStr = `${year}-${month}-${day}`;

  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const timeStr = `${hours}:${minutes}`;

  return {
    date: dateStr,
    time: timeStr,
  };
};
const createAddressDetails = (address?: {
  name: string;
  latitude: number;
  longitude: number;
}): google.maps.places.PlaceResult | undefined => {
  if (!address || !address.name) return undefined;

  return {
    formatted_address: address.name,
    geometry:
      address.latitude && address.longitude
        ? ({
            location: {
              lat: () => address.latitude,
              lng: () => address.longitude,
            } as google.maps.LatLng,
          } as google.maps.places.PlaceGeometry)
        : undefined,
  } as google.maps.places.PlaceResult;
};

export const useActivityToFormData = (activity?: any): CreateActivityFormData => {
  return useMemo(() => {
    if (!activity) return ACTIVITY_INITIAL_VALUES;

    const formData: CreateActivityFormData = {
      ...ACTIVITY_INITIAL_VALUES,
      activityType: activity.stop_type,

      notes: activity.notes || '',
      amountSpent: activity.amount_spend || '',

      email: '',
      phone: '',
      link: '',
    };

    const belongedTravelContact = activity.belonged_travel_contact || activity.travel_contact;
    formData.email = belongedTravelContact?.email || activity.email || '';
    formData.phone = belongedTravelContact?.phone_number || activity.phone_number || '';
    formData.link = belongedTravelContact?.link || activity.link || '';

    switch (activity.stop_type) {
      case 'Flight': {
        formData.flightFrom = activity.flight_from;
        formData.flightTo = activity.flight_to;
        formData.airline = activity.airline_iata_code || '';
        formData.flightNumber = activity.flight_number || '';
        formData.ticketBookingNumber = activity.booking || '';
        formData.seatNumber = activity.seat || '';

        if (activity.flight_from) {
          formData.flightFrom_details = {
            name: activity.flight_from_airport_name || activity.flight_from,
            iata_code: activity.flight_from,
          };
        }
        if (activity.flight_to) {
          formData.flightTo_details = {
            name: activity.flight_to_airport_name || activity.flight_to,
            iata_code: activity.flight_to,
          };
        }
        if (activity.airline) {
          formData.airline_details = {
            name: activity.airline,
            iata_code: activity.airline_iata_code || '',
          };
        }

        const dep = parseDateTime(activity.departure_date_time);
        const arr = parseDateTime(activity.arrival_date_time);
        formData.departureDate = dep.date;
        formData.departureTime = dep.time;
        formData.arrivalDate = arr.date;
        formData.arrivalTime = arr.time;
        break;
      }

      case 'Hotel': {
        formData.name = activity.name;
        formData.address = activity.address?.name || '';
        if (activity.address) formData.address_details = createAddressDetails(activity.address);
        formData.bookingNumber = activity.booking || '';

        const checkIn = parseDateTime(activity.check_in_date_time);
        const checkOut = parseDateTime(activity.check_out_date_time);
        formData.checkInDate = checkIn.date;
        formData.checkInTime = checkIn.time;
        formData.checkOutDate = checkOut.date;
        formData.checkOutTime = checkOut.time;
        break;
      }

      case 'Activity':
      case 'Concert':
      case 'Theatre':
      case 'Meeting':
      case 'Tour': {
        formData.name = activity.name;
        formData.place = activity.place || '';
        formData.address = activity.address?.name || '';
        if (activity.address) formData.address_details = createAddressDetails(activity.address);
        formData.bookingNumber = activity.booking || '';

        const start = parseDateTime(activity.start_date_time);
        const end = parseDateTime(activity.end_date_time);
        formData.startDate = start.date;
        formData.startTime = start.time;
        formData.endDate = end.date;
        formData.endTime = end.time;

        if (activity.stop_type === 'Concert' || activity.stop_type === 'Theatre') {
          formData.seatNumber = activity.seat || '';
        }
        break;
      }

      case 'Restaurant':
      case 'Shopping': {
        formData.name = activity.name || '';
        formData.place = activity.place || '';
        formData.address = activity.address?.name || '';
        if (activity.address) formData.address_details = createAddressDetails(activity.address);
        formData.bookingNumber = activity.booking || '';

        const start = parseDateTime(activity.start_date_time);
        const end = parseDateTime(activity.end_date_time);
        formData.startDate = start.date;
        formData.startTime = start.time;
        formData.endDate = end.date;
        formData.endTime = end.time;
        break;
      }

      case 'CarRental': {
        formData.name = activity.name || '';
        formData.vehicleType = activity.transport_name || '';
        formData.pickupAddress = activity.pick_up_address?.name || '';
        if (activity.pick_up_address) formData.pickupAddress_details = createAddressDetails(activity.pick_up_address);
        formData.dropOffAddress = activity.drop_off_address?.name || '';
        if (activity.drop_off_address)
          formData.dropOffAddress_details = createAddressDetails(activity.drop_off_address);
        formData.officeAddress = activity.address?.name || '';
        if (activity.address) formData.officeAddress_details = createAddressDetails(activity.address);
        formData.bookingNumber = activity.booking || '';

        const start = parseDateTime(activity.start_date_time);
        const end = parseDateTime(activity.end_date_time);
        formData.startDate = start.date;
        formData.startTime = start.time;
        formData.endDate = end.date;
        formData.endTime = end.time;

        if (activity.pick_up_address && activity.drop_off_address) {
          formData.sameAsPickupAddress = activity.pick_up_address.name === activity.drop_off_address.name;
        }
        break;
      }

      case 'Parking':
      case 'Directions': {
        formData.place = activity.place;
        formData.address = activity.address?.name || '';
        if (activity.address) formData.address_details = createAddressDetails(activity.address);
        formData.bookingNumber = activity.booking || '';

        if (activity.stop_type === 'Parking') {
          formData.parkingSpaceNumber = activity.parking_space || '';
        }

        const start = parseDateTime(activity.start_date_time);
        const end = parseDateTime(activity.end_date_time);
        formData.startDate = start.date;
        formData.startTime = start.time;
        formData.endDate = end.date;
        formData.endTime = end.time;
        break;
      }

      case 'BorderCrossing': {
        formData.country_from = activity.country_from;
        formData.country_to = activity.country_to;
        formData.borderCheckpoint = activity.border_checkpoint || '';
        formData.address = activity.address?.name || '';
        if (activity.address) formData.address_details = createAddressDetails(activity.address);

        const start = parseDateTime(activity.start_date_time);
        formData.date = start.date;
        formData.time = start.time;
        break;
      }

      case 'Ferry': {
        formData.name = activity.name;
        formData.carrierName = activity.carrier_name || '';
        formData.vehicleDescription = activity.vehicle_description || '';
        formData.numberOfPassengers = activity.number_of_passengers || '';
        formData.seatNumber = activity.seat || '';
        formData.bookingNumber = activity.booking || '';

        formData.departureLocation = activity.location_from_name || '';
        formData.arrivalLocation = activity.location_to_name || '';
        formData.departureAddress = activity.departure_address?.name || '';
        if (activity.departure_address)
          formData.departureAddress_details = createAddressDetails(activity.departure_address);
        formData.arrivalAddress = activity.arrival_address?.name || '';
        if (activity.arrival_address) formData.arrivalAddress_details = createAddressDetails(activity.arrival_address);

        const dep = parseDateTime(activity.departure_date_time);
        const arr = parseDateTime(activity.arrival_date_time);
        formData.departureDate = dep.date;
        formData.departureTime = dep.time;
        formData.arrivalDate = arr.date;
        formData.arrivalTime = arr.time;
        break;
      }

      case 'Cruise': {
        formData.name = activity.name;
        formData.shipName = activity.transport_name || '';
        formData.cabinType = activity.cabin_type || '';
        formData.cabinNumber = activity.cabin || '';
        formData.bookingNumber = activity.booking || '';

        formData.startingPortName = activity.location_from_name || '';
        formData.endingPortName = activity.location_to_name || '';
        formData.startingPortAddress = activity.starting_port_address?.name || '';
        if (activity.starting_port_address)
          formData.startingPortAddress_details = createAddressDetails(activity.starting_port_address);
        formData.endingPortAddress = activity.ending_port_address?.name || '';
        if (activity.ending_port_address)
          formData.endingPortAddress_details = createAddressDetails(activity.ending_port_address);

        const dep = parseDateTime(activity.departure_date_time);
        const arr = parseDateTime(activity.arrival_date_time);
        formData.departureDate = dep.date;
        formData.departureTime = dep.time;
        formData.arrivalDate = arr.date;
        formData.arrivalTime = arr.time;

        if (activity.starting_port_address && activity.ending_port_address) {
          formData.sameAsStartingPort = activity.starting_port_address.name === activity.ending_port_address.name;
        }
        break;
      }

      case 'Rail': {
        formData.name = activity.name;
        formData.trainType = activity.train_type || '';
        formData.trainNumber = activity.train || '';
        formData.class = activity.seat_type || '';
        formData.seatNumber = activity.seat || '';
        formData.bookingNumber = activity.booking || '';

        formData.departureStation = activity.location_from_name || '';
        formData.arrivalStation = activity.location_to_name || '';
        formData.departureAddress = activity.departure_address?.name || '';
        if (activity.departure_address)
          formData.departureAddress_details = createAddressDetails(activity.departure_address);
        formData.arrivalAddress = activity.arrival_address?.name || '';
        if (activity.arrival_address) formData.arrivalAddress_details = createAddressDetails(activity.arrival_address);

        const dep = parseDateTime(activity.departure_date_time);
        const arr = parseDateTime(activity.arrival_date_time);
        formData.departureDate = dep.date;
        formData.departureTime = dep.time;
        formData.arrivalDate = arr.date;
        formData.arrivalTime = arr.time;
        break;
      }

      case 'Transportation': {
        formData.vehicleType = activity.vehicle_type;
        formData.carrierName = activity.carrier_name || '';
        formData.numberOfPassengers = activity.number_of_passengers || '';
        formData.bookingNumber = activity.booking || '';

        formData.departureLocation = activity.location_from_name || '';
        formData.arrivalLocation = activity.location_to_name || '';
        formData.departureAddress = activity.departure_address?.name || '';
        if (activity.departure_address)
          formData.departureAddress_details = createAddressDetails(activity.departure_address);
        formData.arrivalAddress = activity.arrival_address?.name || '';
        if (activity.arrival_address) formData.arrivalAddress_details = createAddressDetails(activity.arrival_address);

        const dep = parseDateTime(activity.departure_date_time);
        const arr = parseDateTime(activity.arrival_date_time);
        formData.departureDate = dep.date;
        formData.departureTime = dep.time;
        formData.arrivalDate = arr.date;
        formData.arrivalTime = arr.time;
        break;
      }
    }

    return formData;
  }, [activity]);
};
